import { FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Box, Button, IconButton, Menu, Stack, Typography } from '@mui/material';
import { userModel } from 'entities/user';
import {
  BORDER_RADIUS_L,
  COLOR_NEUTRAL_DARKEST,
  flexHelper,
  FontFamilies,
  FontWeights,
  getFormattedRewardDates,
  Icon,
  Spinner,
  Timer,
  useModal,
  useShallowSelector,
  useTimeLeft,
} from 'shared';
import { useWeb3Provider } from 'shared/config/wallet-connect';
import * as dateFns from 'date-fns';

const SWAP_STARTS_ON = '2024-06-18 14:00:00Z';

type TimerWithClaimProps = {
  swapTime?: number;
  isSwaping: boolean;
  isSwapDisabled?: boolean;
  startTime?: number;
  onSwap?: () => void;
};

export const TimerWithClaim: FC<TimerWithClaimProps> = ({
  isSwaping,
  isSwapDisabled,
  onSwap,
  startTime,
  swapTime = 0,
}) => {
  const [isInfoOpen, onOpenInfo, onCloseInfo] = useModal(false);
  const { address } = useShallowSelector(userModel.selectors.selectUserWeb3Info);
  const popoverRef = useRef(null);
  const web3Provider = useWeb3Provider();
  const dispatch = useDispatch();
  useTimeLeft(swapTime * 1000);

  const handleGetAirdropInfo = useCallback(() => {
    dispatch(userModel.userActions.getAirdropInfo({ address, web3Provider }));
  }, [address, dispatch, web3Provider]);

  useEffect(() => {
    if (address) {
      handleGetAirdropInfo();
    }
  }, [address, handleGetAirdropInfo]);

  if (swapTime === 0) return null;

  let formattedCaptionDatesInfo = '';
  if (startTime) {
    const [formattedStart, formattedEnd] = getFormattedRewardDates(startTime);
    formattedCaptionDatesInfo = `between ${formattedStart}, and ${formattedEnd},`;
  }

  return (
    <Stack>
      <Stack
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={(theme) => ({
          border: `1px solid ${theme.themeColors.colorTimerBorder}`,
          py: '19px',
          mt: 1,
          borderRadius: BORDER_RADIUS_L,
        })}
      >
        {startTime ? (
          <Timer title="Swap ends in" date={swapTime} onTimerEnds={handleGetAirdropInfo} />
        ) : (
          <Timer title="Swap starts in" date={dateFns.getUnixTime(new Date(SWAP_STARTS_ON))} />
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ mt: '18px' }}
        alignItems="center"
        justifyContent="center"
        ref={popoverRef}
      >
        <Box>
          <Icon type="gift" size={24} />
        </Box>
        <Typography
          className="dark"
          variant="body2"
          fontWeight={FontWeights.Medium}
          fontFamily={FontFamilies.secondary}
          fontSize={16}
        >
          Get Bonus
        </Typography>
        <IconButton className="transparent" onClick={onOpenInfo} sx={{ width: 24, height: 24 }}>
          <Icon type={isInfoOpen ? 'question-fill' : 'question'} size={24} />
        </IconButton>
      </Stack>
      <Button
        sx={{ width: '100%', mt: 2, '.MuiButton-endIcon': { width: 16, mr: '-8px', ml: 0 } }}
        disabled={isSwapDisabled || !startTime}
        onClick={onSwap}
        {...(isSwaping && { endIcon: <Spinner iconSize={16} sx={{ ...flexHelper('center', 'center') }} /> })}
      >
        Swap
      </Button>
      <Menu
        anchorEl={popoverRef.current}
        open={isInfoOpen}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: 350 },
              padding: '20px 13px 20px 20px',
              background: COLOR_NEUTRAL_DARKEST,
              transform: { xs: 'unset', sm: 'translateX(78px) !important' },
              mt: 0,
            },
          },
        }}
        onClose={onCloseInfo}
      >
        <Stack>
          <Typography className="white" variant="caption">
            All users who hold Mogies or an Access Pass for at least 90 days {formattedCaptionDatesInfo} will be
            rewarded with a MOGUL Airdrop. Each NFT will entitle the holder to 5,000 $MOGUL. You can purchase them here:
          </Typography>
          <NavLink to="https://opensea.io/collection/mogies">
            <Typography className="yellow" variant="caption" sx={{ textDecoration: 'underline' }}>
              https://opensea.io/collection/mogies
            </Typography>
          </NavLink>
          <NavLink to="https://opensea.io/collection/mogul-nft">
            <Typography className="yellow" variant="caption" sx={{ textDecoration: 'underline' }}>
              https://opensea.io/collection/mogul-nft
            </Typography>
          </NavLink>
        </Stack>
      </Menu>
    </Stack>
  );
};
